import { AgeGenderStandalone } from '@/components/age-and-gender';
import { StandaloneParentalConsent } from '@/components/age-and-gender/standalone-parental-consent';
import commonMessages from '@/lang/messages/common';
import { DS_HEIGHT } from '@calm/evexia-design-tokens';
import { Check } from '@mui/icons-material';
import useAccount from '@rhg/hooks/use-account';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useGetCircles } from '@rhg/hooks/use-circles';
import {
    getConsentContext,
    useConsentContext,
} from '@rhg/hooks/use-consent-context';
import { useConsents } from '@rhg/hooks/use-consents';
import useHasAcceptedCohortTos from '@rhg/hooks/use-has-accepted-tos';
import { useGetMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import useToastDialog from '@rhg/hooks/use-toast-dialog';
import { useGetUnsponsoredConfig } from '@rhg/hooks/use-unsponsored-config';
import profileMessages from '@rhg/lang/messages/profile';
import { Box, Dialog, Theme } from '@rhg/libs/material';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const styleIconContainer = {
    height: DS_HEIGHT.AVATAR_MEDIUM,
    width: DS_HEIGHT.AVATAR_MEDIUM,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const styleConfirmContainer = {
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'info.main' : 'info.dark',
    backgroundColor: 'info.light',
    ...styleIconContainer,
};

export enum AgeGenderDialogStep {
    AgeGender = 'AgeGenderStep',
    ParentalConsent = 'ParentalConsentStep',
}

interface AgeGenderDialogProps {
    isOpen: boolean;
    onClose: () => void;
    currentStep?: AgeGenderDialogStep;
}

export function AgeGenderDialog({
    isOpen,
    onClose,
    currentStep = AgeGenderDialogStep.AgeGender,
}: AgeGenderDialogProps) {
    const { formatMessage } = useIntl();
    const { toast } = useToastDialog();
    const { activeCircle } = useActiveCircle();
    const { refetch: refetchConsent } = useHasAcceptedCohortTos();
    const { getCirclesAsync } = useGetCircles();
    const { refetch: refetchAccount } = useAccount();

    const { getMostRecentSponsorshipAsync } = useGetMostRecentSponsorship();
    const { getUnsponsoredConfigAsync } = useGetUnsponsoredConfig();

    const [dialogStep, setCurrentStep] =
        useState<AgeGenderDialogStep>(currentStep);
    const { consents } = useConsents();

    const finishUpdate = useCallback(() => {
        toast({
            title: formatMessage(
                profileMessages['profile.about_you.profile_updated']
            ),
            icon: (
                <Box sx={styleConfirmContainer}>
                    <Check color="inherit" fontSize="large" />
                </Box>
            ),
        });
        onClose();
    }, [formatMessage, toast, onClose]);

    const handleProfileUpdate = useCallback(async () => {
        const consentData = await refetchConsent();
        // Force refresh circles and sponsorships
        await getCirclesAsync(true);
        const consentConfig = activeCircle?.sponsor
            ? await getMostRecentSponsorshipAsync(activeCircle)
            : await getUnsponsoredConfigAsync();
        const consents = consentConfig?.consents;
        const updatedConsentRequirements = getConsentContext({
            accountConsents: consentData.data?.consentData
                ? [consentData.data.consentData]
                : [],
            accountUserConsents: consentData.data?.userResponse
                ? [consentData.data.userResponse]
                : [],
            sponsorConsents: consents,
        });

        await refetchAccount();

        if (updatedConsentRequirements.parental.isRequired) {
            setCurrentStep(AgeGenderDialogStep.ParentalConsent);
            return;
        }

        finishUpdate();
    }, [
        activeCircle,
        finishUpdate,
        getCirclesAsync,
        getMostRecentSponsorshipAsync,
        getUnsponsoredConfigAsync,
        refetchAccount,
        refetchConsent,
    ]);

    const { consent } = useHasAcceptedCohortTos(undefined);
    const accountConsents = useMemo(
        () => (consent?.consentData ? [consent.consentData] : []),
        [consent?.consentData]
    );
    const accountUserConsents = consent?.userResponse
        ? [consent.userResponse]
        : [];

    const { consentRequirements } = useConsentContext({
        accountConsents,
        accountUserConsents,
        sponsorConsents: consents,
    });

    // TODO: In EVE-8765, Add analytics to dialog
    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullScreen
            trackDialog="NotMapped"
        >
            {dialogStep === AgeGenderDialogStep.ParentalConsent ? (
                <StandaloneParentalConsent
                    consentRequirements={consentRequirements}
                    accountConsents={accountConsents}
                    onBack={() => setCurrentStep(AgeGenderDialogStep.AgeGender)}
                    onClose={finishUpdate}
                />
            ) : (
                <AgeGenderStandalone
                    onCancel={onClose}
                    onConfirm={handleProfileUpdate}
                    primaryCtaLabel={formatMessage(
                        commonMessages['common.save']
                    )}
                    primaryCtaStartIcon={<Check />}
                    showAddEditButtons
                    sx={{ px: { xs: 2, md: 0 } }}
                    title={formatMessage(
                        profileMessages[
                            'profile.about_you.dob_and_gender_dialog_title'
                        ]
                    )}
                    heading={formatMessage(
                        profileMessages[
                            'profile.about_you.dobAndGender_lower_case'
                        ]
                    )}
                />
            )}
        </Dialog>
    );
}

export default AgeGenderDialog;
